/**
 * Routing ledger: local, append-only outcome measurements per model.
 *
 * Measured routing v2 — instead of ranking failover candidates purely on
 * static heuristics (cost band, reasoning, context window), Elyra records how
 * models actually perform on this machine (turn success, edit failures,
 * retries, failovers away from a model) and uses those measurements to demote
 * demonstrably unreliable candidates.
 *
 * Privacy: only model/provider ids, counters, timings, and cost numbers are
 * stored — never prompts, file paths, or code. The ledger is a single local
 * JSONL file under the agent dir and never leaves disk.
 */

import { appendFileSync, existsSync, mkdirSync, readFileSync, statSync, writeFileSync } from "node:fs";
import { dirname } from "node:path";

// ── Event types ─────────────────────────────────────────────────────────────

/** Task classification. Reserved prefixes keep the schema stable when subagents/swarm gain per-role model choice. */
export type LedgerTaskClass = "main" | "review" | `subagent:${string}` | `swarm:${string}`;

export interface LedgerTurnEvent {
	type: "turn";
	ts: number;
	provider: string;
	modelId: string;
	taskClass: LedgerTaskClass;
	stopReason: string;
	aborted: boolean;
	error: boolean;
	latencyMs?: number;
	inputTokens: number;
	outputTokens: number;
	cost: number;
	editsAttempted: number;
	editsFailed: number;
	retries: number;
}

export interface LedgerFailoverEvent {
	type: "failover";
	ts: number;
	fromProvider: string;
	fromModelId: string;
	toProvider?: string;
	toModelId?: string;
}

export type LedgerEvent = LedgerTurnEvent | LedgerFailoverEvent;

// ── Storage ─────────────────────────────────────────────────────────────────

/** Rotate when the file grows beyond this size; keep the newest half. */
const ROTATE_BYTES = 1_048_576;
/** Only events newer than this window contribute to stats. */
const STATS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
/** Below this many turns a model is never demoted (insufficient evidence). */
export const MIN_RELIABILITY_SAMPLE = 5;

export function appendLedgerEvent(path: string, event: LedgerEvent): void {
	mkdirSync(dirname(path), { recursive: true });
	rotateIfNeeded(path);
	appendFileSync(path, `${JSON.stringify(event)}\n`, "utf-8");
}

function rotateIfNeeded(path: string): void {
	try {
		if (!existsSync(path)) return;
		if (statSync(path).size < ROTATE_BYTES) return;
		const lines = readFileSync(path, "utf-8")
			.split("\n")
			.filter((l) => l.length > 0);
		const keep = lines.slice(Math.floor(lines.length / 2));
		writeFileSync(path, keep.length > 0 ? `${keep.join("\n")}\n` : "", "utf-8");
	} catch {
		// Rotation is best-effort; a failed rotation must never block recording.
	}
}

export function readLedger(path: string): LedgerEvent[] {
	if (!existsSync(path)) return [];
	let raw: string;
	try {
		raw = readFileSync(path, "utf-8");
	} catch {
		return [];
	}
	const events: LedgerEvent[] = [];
	for (const line of raw.split("\n")) {
		if (!line.trim()) continue;
		try {
			const parsed = JSON.parse(line) as LedgerEvent;
			if (parsed && (parsed.type === "turn" || parsed.type === "failover")) events.push(parsed);
		} catch {
			// Skip corrupt lines
		}
	}
	return events;
}

// ── Aggregation ─────────────────────────────────────────────────────────────

export interface ModelOutcomeStats {
	turns: number;
	/** Fraction of turns that ended without error and without being aborted. */
	successRate: number;
	/** Times a failover moved *away* from this model. */
	failoversFrom: number;
	editsAttempted: number;
	editsFailed: number;
	retries: number;
	totalCost: number;
	avgLatencyMs?: number;
}

function statsKey(provider: string, modelId: string): string {
	return `${provider}/${modelId}`;
}

export function computeStats(
	events: readonly LedgerEvent[],
	options?: { taskClass?: LedgerTaskClass; now?: number },
): Map<string, ModelOutcomeStats> {
	const now = options?.now ?? Date.now();
	const cutoff = now - STATS_WINDOW_MS;

	interface Acc {
		turns: number;
		successes: number;
		failoversFrom: number;
		editsAttempted: number;
		editsFailed: number;
		retries: number;
		totalCost: number;
		latencySum: number;
		latencyCount: number;
	}
	const acc = new Map<string, Acc>();
	const get = (key: string): Acc => {
		let a = acc.get(key);
		if (!a) {
			a = {
				turns: 0,
				successes: 0,
				failoversFrom: 0,
				editsAttempted: 0,
				editsFailed: 0,
				retries: 0,
				totalCost: 0,
				latencySum: 0,
				latencyCount: 0,
			};
			acc.set(key, a);
		}
		return a;
	};

	for (const event of events) {
		if (event.ts < cutoff) continue;
		if (event.type === "turn") {
			if (options?.taskClass && event.taskClass !== options.taskClass) continue;
			const a = get(statsKey(event.provider, event.modelId));
			a.turns++;
			if (!event.error && !event.aborted) a.successes++;
			a.editsAttempted += event.editsAttempted;
			a.editsFailed += event.editsFailed;
			a.retries += event.retries;
			a.totalCost += event.cost;
			if (typeof event.latencyMs === "number") {
				a.latencySum += event.latencyMs;
				a.latencyCount++;
			}
		} else {
			const a = get(statsKey(event.fromProvider, event.fromModelId));
			a.failoversFrom++;
		}
	}

	const result = new Map<string, ModelOutcomeStats>();
	for (const [key, a] of acc) {
		result.set(key, {
			turns: a.turns,
			successRate: a.turns > 0 ? a.successes / a.turns : 1,
			failoversFrom: a.failoversFrom,
			editsAttempted: a.editsAttempted,
			editsFailed: a.editsFailed,
			retries: a.retries,
			totalCost: a.totalCost,
			avgLatencyMs: a.latencyCount > 0 ? a.latencySum / a.latencyCount : undefined,
		});
	}
	return result;
}

export function loadStats(path: string, taskClass?: LedgerTaskClass): Map<string, ModelOutcomeStats> {
	return computeStats(readLedger(path), taskClass ? { taskClass } : undefined);
}

// ── Reporting ───────────────────────────────────────────────────────────────

/** Plain-text stats report shared by `elyra doctor routing` and `/routing-stats`. */
export function formatStatsReport(stats: ReadonlyMap<string, ModelOutcomeStats>): string {
	if (stats.size === 0) {
		return "No routing measurements recorded yet. Stats accumulate as you use Elyra.";
	}

	const rows = [...stats.entries()].sort((a, b) => b[1].turns - a[1].turns);
	const lines: string[] = [];
	lines.push("Model outcome measurements (last 30 days, local only):");
	lines.push("");
	for (const [key, s] of rows) {
		const success = `${Math.round(s.successRate * 100)}%`;
		const editRate = s.editsAttempted > 0 ? `${s.editsFailed}/${s.editsAttempted} edit failures` : "no edits";
		const latency = s.avgLatencyMs !== undefined ? `${(s.avgLatencyMs / 1000).toFixed(1)}s avg` : "n/a";
		const demoted =
			s.turns >= MIN_RELIABILITY_SAMPLE && (s.successRate < 0.8 || s.failoversFrom / s.turns > 0.5)
				? "  [demoted in failover ranking]"
				: "";
		lines.push(
			`- ${key}: ${s.turns} turn${s.turns === 1 ? "" : "s"}, ${success} success, ${editRate}, ` +
				`${s.retries} retr${s.retries === 1 ? "y" : "ies"}, ${s.failoversFrom} failover${s.failoversFrom === 1 ? "" : "s"} away, ` +
				`$${s.totalCost.toFixed(4)} total, ${latency}${demoted}`,
		);
	}
	return lines.join("\n");
}

// ── Turn outcome recorder ───────────────────────────────────────────────────

interface RecorderEvent {
	type: string;
	message?: {
		role?: string;
		toolName?: string;
		isError?: boolean;
		provider?: string;
		model?: string;
		stopReason?: string;
		usage?: { input?: number; output?: number; cost?: { total?: number } };
	};
	attempt?: number;
}

const EDIT_TOOLS = new Set(["edit", "write"]);

/**
 * Consumes AgentSession events and appends one ledger turn event per
 * assistant message. Tool results that follow an assistant message are
 * attributed to it; the record is flushed when the next assistant message
 * starts or the run ends.
 */
export class TurnOutcomeRecorder {
	private turnStartTs: number | undefined;
	private pending: LedgerTurnEvent | undefined;

	constructor(
		private readonly ledgerPath: string,
		private readonly taskClass: LedgerTaskClass = "main",
	) {}

	/** Handle an AgentSession event. Never throws. */
	handleEvent(rawEvent: unknown): void {
		try {
			this.process(rawEvent as RecorderEvent);
		} catch {
			// Recording must never break the session.
		}
	}

	private process(event: RecorderEvent): void {
		if (event.type === "turn_start") {
			this.turnStartTs = Date.now();
			return;
		}

		if (event.type === "auto_retry_end" && this.pending && typeof event.attempt === "number") {
			this.pending.retries = Math.max(this.pending.retries, event.attempt);
			return;
		}

		if (event.type === "agent_end") {
			this.flush();
			return;
		}

		if (event.type !== "message_end" || !event.message) return;
		const message = event.message;

		if (message.role === "assistant") {
			this.flush();
			const now = Date.now();
			this.pending = {
				type: "turn",
				ts: now,
				provider: message.provider ?? "unknown",
				modelId: message.model ?? "unknown",
				taskClass: this.taskClass,
				stopReason: message.stopReason ?? "unknown",
				aborted: message.stopReason === "aborted",
				error: message.stopReason === "error",
				latencyMs: this.turnStartTs !== undefined ? now - this.turnStartTs : undefined,
				inputTokens: message.usage?.input ?? 0,
				outputTokens: message.usage?.output ?? 0,
				cost: message.usage?.cost?.total ?? 0,
				editsAttempted: 0,
				editsFailed: 0,
				retries: 0,
			};
			return;
		}

		if (message.role === "toolResult" && this.pending && message.toolName && EDIT_TOOLS.has(message.toolName)) {
			this.pending.editsAttempted++;
			if (message.isError) this.pending.editsFailed++;
		}
	}

	/** Flush the pending turn record to disk, if any. */
	flush(): void {
		if (!this.pending) return;
		const record = this.pending;
		this.pending = undefined;
		try {
			appendLedgerEvent(this.ledgerPath, record);
		} catch {
			// Best-effort persistence.
		}
	}
}
